import { Briefcase, Building2, Calendar, Layers, Loader2, Mail, MapPin, UserRound, Users } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';
import { useWorkplaceApprovalsQuery, useWorkplaceMeQuery } from '@/client/api/hr/people';
import { cn } from '@/lib/utils';
import { ApprovalsTab } from './ApprovalsTab';
import { DocumentsTab, EquipmentPanel } from './DocumentsTab';
import { ExpensesTab } from './ExpensesTab';
import { PayTab } from './PayTab';
import { PerformanceTab } from './PerformanceTab';
import { TimeOffTab } from './TimeOffTab';
import { EmployeeAvatar, EmptyState, Panel, ProgressBar, StatusBadge } from '../HumanResource/components/hr-ui';
import {
  CHECKLIST_STATUS_BADGE,
  CHECKLIST_STATUS_LABEL,
  EMPLOYMENT_STATUS_BADGE,
  EMPLOYMENT_STATUS_LABEL,
  EMPLOYMENT_TYPE_LABEL,
  ONBOARDING_CATEGORY_LABEL,
  type WorkplacePerson,
} from '@/client/types/hr';

function Person({ person, hint }: { person: WorkplacePerson; hint?: string }) {
  return (
    <div className="flex items-center gap-3">
      <EmployeeAvatar employee={person} size="sm" />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">{person.name}</p>
        <p className="truncate text-xs text-gray-500">
          {[hint, person.title, person.email].filter(Boolean).join(' · ')}
        </p>
      </div>
    </div>
  );
}

const TABS = ['profile', 'time-off', 'pay', 'performance', 'expenses', 'documents', 'approvals'] as const;
type Tab = (typeof TABS)[number];

/** My workplace: every console user's own HR self-service. The API only ever
 *  returns the caller, so nothing here needs an `hr.*` permission. */
const WorkplacePage = () => {
  useBreadcrumbs([{ label: 'Admin', href: '/admin' }, { label: 'My workplace' }]);
  const meQ = useWorkplaceMeQuery();
  const me = meQ.data;
  const employee = me?.employee ?? null;
  const tasks = me?.tasks ?? [];
  const done = tasks.filter((t) => t.status === 'completed').length;
  const isManager = (me?.directReports.length ?? 0) > 0;
  const approvalsQ = useWorkplaceApprovalsQuery(isManager);
  const waiting = (approvalsQ.data?.pending.length ?? 0) + (approvalsQ.data?.expenses?.length ?? 0);
  const [tab, setTab] = useTabSearchParam<Tab>(TABS, 'profile');
  const tabs: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'time-off', label: 'Time off' },
    { id: 'pay', label: 'Pay' },
    { id: 'performance', label: 'Performance' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'documents', label: 'Documents' },
    ...(isManager ? [{ id: 'approvals' as Tab, label: waiting ? `Approvals (${waiting})` : 'Approvals' }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-blue-700" />
          <h1 className="text-2xl font-bold text-gray-900">My workplace</h1>
        </div>
        <p className="ml-10 max-w-2xl text-sm text-gray-500">
          Your employee record, your team, and the things HR has asked you to do. Ask HR to correct anything here.
        </p>
      </div>

      {meQ.isLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your employee record…
        </div>
      ) : meQ.isError ? (
        <EmptyState label={`Could not load your workplace: ${(meQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : !employee ? (
        <EmptyState label="You are not in the employee directory yet. Ask HR to add you with this account." />
      ) : (
        <>
        <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'time-off' ? (
          <TimeOffTab />
        ) : tab === 'pay' ? (
          <PayTab name={employee.name} />
        ) : tab === 'expenses' ? (
          <ExpensesTab />
        ) : tab === 'documents' ? (
          <DocumentsTab />
        ) : tab === 'performance' ? (
          <PerformanceTab me={employee.id} reports={me?.directReports ?? []} />
        ) : tab === 'approvals' && isManager ? (
          <ApprovalsTab reports={me?.directReports ?? []} />
        ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <Panel title="Me" className="lg:col-span-1">
            <div className="mb-4 flex items-center gap-3">
              <EmployeeAvatar employee={employee} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-base font-semibold text-gray-900">{employee.name}</p>
                  <StatusBadge label={EMPLOYMENT_STATUS_LABEL[employee.status]} className={EMPLOYMENT_STATUS_BADGE[employee.status]} />
                </div>
                <p className="truncate text-sm text-gray-500">{employee.title || 'No title yet'}</p>
              </div>
            </div>
            <dl className="space-y-2.5 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-gray-400" /> {employee.department || 'No department yet'}
              </div>
              <div className="flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 text-gray-400" /> {employee.level || 'No level yet'}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-gray-400" /> {employee.email || 'No email on file'}
              </div>
              {employee.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-gray-400" /> {employee.location}
                </div>
              )}
              {employee.startDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" /> Started {new Date(employee.startDate).toLocaleDateString()}
                </div>
              )}
              <div className="flex items-center gap-2">
                <UserRound className="h-3.5 w-3.5 text-gray-400" /> {EMPLOYMENT_TYPE_LABEL[employee.employmentType]}
              </div>
            </dl>
          </Panel>

          <Panel title="My team" className="lg:col-span-1">
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Manager</p>
                {me?.manager ? <Person person={me.manager} /> : <p className="text-sm text-gray-500">No manager set.</p>}
              </div>
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
                  <Users className="h-3.5 w-3.5" /> Reports to me ({me?.directReports.length ?? 0})
                </p>
                {me?.directReports.length ? (
                  <div className="space-y-2.5">
                    {me.directReports.map((p) => (
                      <Person key={p.id} person={p} hint={p.department} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Nobody reports to you.</p>
                )}
              </div>
            </div>
          </Panel>

          <Panel
            title="My checklist"
            className="lg:col-span-1"
            action={tasks.length ? <span className="text-xs text-gray-500">{done} of {tasks.length} done</span> : undefined}
          >
            {tasks.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Nothing on your checklist.</p>
            ) : (
              <>
                <ProgressBar value={(done / tasks.length) * 100} className="mb-3" />
                <ul className="space-y-2">
                  {tasks.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate text-gray-800">{t.title}</p>
                        <p className="text-[11px] text-gray-400">
                          {t.kind === 'onboarding' ? 'Onboarding' : 'Offboarding'} · {ONBOARDING_CATEGORY_LABEL[t.category]}
                          {t.dueDate && ` · Due ${new Date(t.dueDate).toLocaleDateString()}`}
                        </p>
                      </div>
                      <StatusBadge label={CHECKLIST_STATUS_LABEL[t.status]} className={CHECKLIST_STATUS_BADGE[t.status]} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="mt-3 text-xs text-gray-400">HR ticks these off as they are done.</p>
          </Panel>
          <div className="lg:col-span-3">
            <EquipmentPanel />
          </div>
        </div>
        )}
        </>
      )}
    </div>
  );
};

export default WorkplacePage;
